// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useConnectivityStore } from './connectivityStore';
import { useRoomSpectatorStore } from './roomSpectatorStore';
import { logger } from '../lib/logger';
import type { StateStorage } from 'zustand/middleware';

export interface GameSessionStore {
  sessionId: string | null;
  roomCode: string | null;
  playerId: string | null;
  playerToken: string | null;
  displayName: string | null;
  sessionRestorePending: boolean;
  isPublic: boolean;
  sessionError: string | null;
  showSessionErrorModal: boolean;

  setSession: (
    sessionId: string,
    roomCode: string,
    playerId: string,
    playerToken: string,
    displayName: string
  ) => void;
  setSessionId: (sessionId: string) => void;
  setRoomCode: (roomCode: string) => void;
  setPlayerId: (playerId: string) => void;
  setPlayerToken: (playerToken: string) => void;
  setDisplayName: (displayName: string) => void;
  setSessionRestorePending: (pending: boolean) => void;
  clearSession: () => void;
  setSessionError: (error: string) => void;
  clearSessionError: () => void;
  setRoomPrivacy: (isPublic: boolean) => void;
  toggleRoomPrivacy: () => void;
}

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

function getSessionStorage(): StateStorage {
  if (typeof window === 'undefined') {
    return noopStorage;
  }
  try {
    return window.sessionStorage;
  } catch {
    return noopStorage;
  }
}

export const useGameSessionStore = create<GameSessionStore>()(
  persist(
    (set, get) => ({
      sessionId: null,
      roomCode: null,
      playerId: null,
      playerToken: null,
      displayName: null,
      sessionRestorePending: false,
      isPublic: true,
      sessionError: null,
      showSessionErrorModal: false,

      setSession: (sessionId, roomCode, playerId, playerToken, displayName) => {
        logger.info('session_set', { sessionId, roomCode, playerId, displayName });
        set({
          sessionId,
          roomCode,
          playerId,
          playerToken,
          displayName,
          sessionRestorePending: false,
        });
      },

      setSessionId: (sessionId: string) => {
        logger.info('session_id_set', { sessionId });
        set({ sessionId });
      },

      setRoomCode: (roomCode: string) => {
        logger.info('room_code_set', { roomCode });
        set({ roomCode });
      },

      setPlayerId: (playerId: string) => {
        logger.info('player_id_set', { playerId });
        set({ playerId });
      },

      setPlayerToken: (playerToken: string) => {
        logger.info('player_token_set', { hasToken: !!playerToken });
        set({ playerToken });
      },

      setDisplayName: (displayName: string) => {
        logger.info('display_name_set', { displayName });
        set({ displayName });
      },

      setSessionRestorePending: pending => {
        logger.debug('session_restore_pending_set', { pending });
        set({ sessionRestorePending: pending });
      },

      clearSession: () => {
        set({
          sessionId: null,
          roomCode: null,
          playerId: null,
          playerToken: null,
          displayName: null,
          sessionRestorePending: false,
        });
        useConnectivityStore.getState().setConnected(false);
        useRoomSpectatorStore.getState().reset();
      },

      setSessionError: error => {
        logger.error('session_error', { error });
        set({ sessionError: error, showSessionErrorModal: true });
      },

      clearSessionError: () => {
        set({ sessionError: null, showSessionErrorModal: false });
      },

      setRoomPrivacy: isPublic => {
        logger.info('room_privacy_set', { isPublic });
        set({ isPublic });
      },

      toggleRoomPrivacy: () => {
        const { isPublic } = get();
        logger.info('room_privacy_toggle', {
          currentPrivacy: isPublic,
          newPrivacy: !isPublic,
        });
      },
    }),
    {
      name: 'undef-game-storage',
      storage: createJSONStorage(getSessionStorage),
      partialize: state => ({
        sessionId: state.sessionId,
        roomCode: state.roomCode,
        playerId: state.playerId,
        playerToken: state.playerToken,
        displayName: state.displayName,
      }),
    }
  )
);
